import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type ProjectSummary, useDeleteAdminProject } from "@/lib/api";
import { logger } from "@/lib/logger";

interface ProjectDeleteDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectDeleteDialog({ project, open, onOpenChange }: ProjectDeleteDialogProps) {
  const deleteProject = useDeleteAdminProject();
  const [error, setError] = useState("");
  const deleting = deleteProject.isPending;

  const handleDelete = async (e: React.MouseEvent) => {
    // AlertDialogAction closes the dialog on click by default — prevent that
    // so a failed delete keeps the dialog open with the error visible, and
    // close manually on success instead.
    e.preventDefault();
    if (!project) {
      return;
    }
    setError("");

    try {
      await deleteProject.mutateAsync({ projectId: project.id });
      onOpenChange(false);
    } catch (err) {
      logger.error("[project-delete] failed:", err);
      setError("Failed to delete project. Please try again.");
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Don't let a stale error greet the next open.
      setError("");
    }
    onOpenChange(next);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Project</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{project?.project_name}</strong>? This removes
            the project record from the database only — verify its GitHub repo and Supabase/Railway
            resources are already gone (or clean them up first), because they are not touched by
            this action.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete Project"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
